import React, { useState, useEffect } from "react";
import {
  Folder,
  FolderOpen,
  Plus,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import { Skeleton } from "../common/Skeleton";

const MAX_SECTION_DEPTH = 2; // Maximum 2 levels of subsections allowed

const SectionTree = ({
  sections,
  currentSectionId,
  onSectionSelect,
  onCreateSection,
  onEditSection,
  onDeleteSection,
  onDropTestCase,
  loading = false,
  projectStructure = null,
}) => {
  // Initialize expanded sections with first-level sections (root sections)
  const getFirstLevelSectionIds = (sectionList) => {
    const ids = new Set();
    sectionList.forEach((section) => {
      const sectionId = section.section_id || section.folder_id;
      const parentSectionId =
        section.parent_section_id !== undefined
          ? section.parent_section_id
          : section.parent_folder_id;
      if (!parentSectionId && sectionId) {
        ids.add(sectionId);
      }
    });
    return ids;
  };

  const [expandedSections, setExpandedSections] = useState(() =>
    getFirstLevelSectionIds(sections),
  );
  const [hoveredSectionId, setHoveredSectionId] = useState(null);

  // Update expanded sections when sections change (expand first level)
  useEffect(() => {
    if (sections && sections.length > 0) {
      const firstLevelIds = getFirstLevelSectionIds(sections);
      setExpandedSections((prev) => {
        const newSet = new Set(prev);
        firstLevelIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    }
  }, [sections]);

  const getSectionDepth = (section) => {
    if (!section) return 0;
    const segments = (section.path || "")
      .split("/")
      .map((segment) => segment.trim())
      .filter(Boolean);
    return Math.max(0, segments.length - 1);
  };

  const toggleSection = (sectionId, e) => {
    e.stopPropagation();
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const handleSectionClick = (sectionId) => {
    onSectionSelect(sectionId);
  };

  const handleCreateSubsection = (parentSectionId, e) => {
    e.stopPropagation();
    if (onCreateSection && typeof onCreateSection === "function") {
      onCreateSection(parentSectionId);
    }
  };

  const renderSection = (section, level = 0) => {
    // Map folder_id to section_id for backward compatibility with API responses
    const sectionId = section.section_id || section.folder_id;
    const parentSectionId =
      section.parent_section_id !== undefined
        ? section.parent_section_id
        : section.parent_folder_id;

    const isExpanded = expandedSections.has(sectionId);
    const hasChildren = section.children && section.children.length > 0;
    const isSelected =
      currentSectionId != null &&
      String(currentSectionId).trim() === String(sectionId ?? "").trim();
    const isHovered = hoveredSectionId === sectionId;
    const isRoot = parentSectionId === null || section.is_root;
    const sectionDepth = getSectionDepth(section);
    const canCreateChild = sectionDepth < MAX_SECTION_DEPTH;
    const iconWrapperClasses =
      "flex items-center justify-center rounded-md flex-shrink-0 transition-colors bg-transparent text-primary-1000";

    return (
      <div key={sectionId} className="select-none">
        <div
          className={`flex items-center justify-between cursor-pointer group rounded-lg transition-colors ${
            isSelected
              ? "bg-[#eef2ff] font-medium text-[#3730a3]"
              : "bg-transparent text-primary-1000 text-text-primary hover:bg-[#f5f5f7]"
          }`}
          style={{
            marginLeft: `${level * 20}px`,
            height: "36px",
            padding: "0 12px",
            borderRadius: "8px",
          }}
          onClick={() => handleSectionClick(sectionId)}
          aria-current={isSelected ? "true" : undefined}
          onMouseEnter={() => setHoveredSectionId(sectionId)}
          onMouseLeave={() => setHoveredSectionId(null)}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.add(
              "bg-blue-50",
              "border-2",
              "border-blue-300",
              "border-dashed",
            );
          }}
          onDragLeave={(e) => {
            e.currentTarget.classList.remove(
              "bg-blue-50",
              "border-2",
              "border-blue-300",
              "border-dashed",
            );
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.classList.remove(
              "bg-blue-50",
              "border-2",
              "border-blue-300",
              "border-dashed",
            );
            const testCaseId = e.dataTransfer.getData("text/plain");
            if (testCaseId && onDropTestCase) {
              onDropTestCase(testCaseId, sectionId);
            }
          }}
        >
          <div className="flex items-center flex-1 min-w-0">
            <button
              onClick={(e) => toggleSection(sectionId, e)}
              className="p-1 hover:bg-bg-hover rounded flex-shrink-0"
              style={{ marginRight: "4px" }}
            >
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )
              ) : (
                <div className="w-4 h-4" />
              )}
            </button>
            <span
              className={iconWrapperClasses}
              style={{
                width: "28px",
                height: "28px",
                marginRight: "8px",
              }}
            >
              {isExpanded || !hasChildren ? (
                <FolderOpen className="w-4 h-4" />
              ) : (
                <Folder className="w-4 h-4" />
              )}
            </span>
            <span
              className={`text-sm truncate ${isSelected ? "font-medium" : "font-light"}`}
            >
              {section.name}
            </span>
            {section.test_cases_count > 0 && (
              <span className="ml-2 text-xs text-text-primary">
                ({section.test_cases_count})
              </span>
            )}
          </div>
          {(isHovered || isSelected) && (
            <div className="flex items-center space-x-1 flex-shrink-0">
              {
                onCreateSection &&
                typeof onCreateSection === "function" && (
                  <button
                    onClick={(e) => handleCreateSubsection(sectionId, e)}
                    className="p-1 hover:bg-indigo-200 rounded text-text-secondary hover:text-indigo-700"
                    title="Create subsection"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              {onEditSection && typeof onEditSection === "function" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditSection(section);
                  }}
                  className="p-1 hover:bg-indigo-200 rounded text-gray-600 hover:text-indigo-700"
                  title="Edit section"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              )}
              {onDeleteSection && typeof onDeleteSection === "function" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSection(section);
                  }}
                  className="p-1 hover:bg-red-200 rounded text-text-secondary hover:text-red-700"
                  title="Delete section"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div style={{ position: "relative", marginTop: "4px" }}>
            <div
              style={{
                position: "absolute",
                left: `${level * 20 + 24}px`,
                top: 0,
                bottom: 0,
                width: "1.5px",
                backgroundColor: "#D1D5DB",
                pointerEvents: "none",
              }}
            />
            {section.children.map((child) => renderSection(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const isMultiSuite =
    projectStructure === "MULTIPLE_TEST_SUITES" ||
    projectStructure === "MULTI_SUITE";
  const entityName = isMultiSuite ? "suites" : "sections";
  const createButtonText = isMultiSuite ? "Create Suite" : "Create Section";

  if (loading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="mx-auto h-6 w-3/4 max-w-xs" />
        <Skeleton className="mx-auto h-6 w-2/3 max-w-xs" />
        <Skeleton className="mx-auto h-6 w-4/5 max-w-xs" />
      </div>
    );
  }

  if (!sections || sections.length === 0) {
    return (
      <div className="p-4 text-center text-text-secondary">
        <Folder className="w-12 h-12 mx-auto mb-2 text-text-tertiary" />
        <p className="text-sm">No {entityName} yet</p>
        {onCreateSection && typeof onCreateSection === "function" && (
          <button
            onClick={() => onCreateSection(null)}
            className="mt-2 px-3 py-1 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            {createButtonText}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="py-2">
      {/* All Test Cases root item */}
      {/* <div
        className={`flex items-center cursor-pointer group transition-colors rounded-lg mx-1 mb-1 ${
          currentSectionId === null
            ? 'bg-[#A17561FF] text-white'
            : 'text-text-primary hover:bg-bg-hover'
        }`}
        style={{ height: '36px', padding: '0 12px' }}
        onClick={() => onSectionSelect(null)}
      >
        <div className="w-4 h-4 mr-1 flex-shrink-0" />
        <span className={`flex items-center justify-center rounded-md flex-shrink-0 mr-2 ${currentSectionId === null ? 'text-white' : ''}`} style={{ width: '28px', height: '28px' }}>
          <FolderOpen className="w-4 h-4" />
        </span>
        <span className="text-sm font-light truncate">All Test Cases</span>
      </div> */}
      {sections.map((section) => renderSection(section))}
    </div>
  );
};

export default SectionTree;
